import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import ArrowLeft from '@rover/icons/svg/arrow-left.svg';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet/BottomSheet';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import {
  RECURRING_SWITCH_INFO_CONTENT,
  RECURRING_SWITCH_INFO_TITLE,
} from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/constants';

import RevampDatesPicker from './RevampDatesPicker';
import RevampDatesSaveButton from './RevampDatesSaveButton';
import useRevampDatesSelection, { DateRange, DateSelection } from './useRevampDatesSelection';

const MONTHS_SHOWN = 1;

const ArrowLeftIcon = (): JSX.Element => <ArrowLeft width="24px" height="24px" />;

export type { DateRange, DateSelection };

type Props = DateSelection & {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selection: DateSelection) => void;
  locale: Locale;
  allowsLooseDates?: boolean;
  allowsRecurring?: boolean;
};

const RevampDatesSheet = ({
  isOpen,
  onClose,
  onSave,
  locale,
  startDate,
  endDate,
  dates,
  isRecurring,
  weekDays,
  allowsLooseDates,
  allowsRecurring,
}: Props): JSX.Element => {
  const { i18n, t } = useLingui();
  const headingId = useId();
  const [isShowingRecurringInfo, setIsShowingRecurringInfo] = useState(false);
  const selection = useRevampDatesSelection({
    isOpen,
    onClose,
    onSave,
    startDate,
    endDate,
    dates,
    isRecurring,
    weekDays,
    allowsLooseDates,
    allowsRecurring,
  });

  const infoTriggerRef = useRef<HTMLElement>(null);
  const backButtonRef = useRef<HTMLElement>(null);
  const wasShowingRecurringInfo = useRef(false);

  useEffect(() => {
    if (!isOpen) setIsShowingRecurringInfo(false);
  }, [isOpen]);

  useEffect(() => {
    if (isShowingRecurringInfo) backButtonRef.current?.focus();
    else if (wasShowingRecurringInfo.current) infoTriggerRef.current?.focus();
    wasShowingRecurringInfo.current = isShowingRecurringInfo;
  }, [isShowingRecurringInfo]);

  const showRecurringInfo = useCallback(() => setIsShowingRecurringInfo(true), []);
  const hideRecurringInfo = useCallback(() => setIsShowingRecurringInfo(false), []);

  return (
    <BottomSheet
      isOpen={isOpen}
      onRequestClose={onClose}
      headerId={headingId}
      // Repeating dates add a switch, the weekdays and a start date above the calendar,
      // which together outgrow the sheet: its content scrolls, and the button that saves
      // them stays pinned to the footer instead of being pushed off screen.
      overflowY="auto"
      customHeader={
        isShowingRecurringInfo ? (
          <Flex alignItems="center" gap="2x">
            <Button
              variant="flat"
              size="small"
              icon={ArrowLeftIcon}
              onPress={hideRecurringInfo}
              testID="revamp-dates-sheet-info-back"
              p="0x"
              webProps={{ 'aria-label': t`Go back to dates`, ref: backButtonRef }}
            />
            <Heading size="300" id={headingId} mb="0x">
              {i18n._(RECURRING_SWITCH_INFO_TITLE)}
            </Heading>
          </Flex>
        ) : undefined
      }
      customFooter={
        isShowingRecurringInfo ? undefined : (
          <Box pt="2x">
            <RevampDatesSaveButton selection={selection} />
          </Box>
        )
      }
    >
      {isShowingRecurringInfo ? (
        <Paragraph size="200" data-testid="revamp-dates-sheet-recurring-info">
          {i18n._(RECURRING_SWITCH_INFO_CONTENT)}
        </Paragraph>
      ) : (
        <>
          <A11yHiddenBox webAs="h2" id={headingId}>
            <Trans>Select dates</Trans>
          </A11yHiddenBox>
          <RevampDatesPicker
            selection={selection}
            locale={locale}
            allowsLooseDates={allowsLooseDates}
            allowsRecurring={allowsRecurring}
            onRecurringInfoPress={showRecurringInfo}
            recurringInfoTriggerRef={infoTriggerRef}
            monthsShown={MONTHS_SHOWN}
            isMobileBrowser
            testID="revamp-dates-sheet"
          />
        </>
      )}
    </BottomSheet>
  );
};

export default RevampDatesSheet;
